import * as React from "react";
import { Sliders, AlertCircle, AlertTriangle, CheckCircle } from "react-feather";
import { useAppSelector } from "../../store/hooks";
import { selectProcessedQuotas } from "../../store/healthSlice";
import { HEALTH_COLORS } from "./utils";
import { Loader } from "../../components/layout/loader";

export const Quotas: React.FC = () => {
  const isLoading = useAppSelector((state) => state.health.isLoading);
  const isFetched = useAppSelector((state) => state.health.isFetched);
  const quotas = useAppSelector((state) => state.health.quotas);
  const processedQuotas = useAppSelector(selectProcessedQuotas);

  // Categorize quotas
  const categorizedQuotas = React.useMemo(() => {
    const exceeded = processedQuotas.filter((q) => q.error);
    const nearLimit = processedQuotas.filter((q) => q.warning && !q.error);
    const ok = quotas.length - exceeded.length - nearLimit.length;
    return { exceeded, nearLimit, ok };
  }, [processedQuotas, quotas]);

  if (isLoading && !isFetched) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader />
      </div>
    );
  }

  return (
    <div className="pb-16" style={{ maxWidth: 1000 }}>
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-2 mb-2">
          <Sliders size={20} style={{ color: "var(--color-label)" }} />
          <h1 className="text-xl font-semibold" style={{ color: "var(--color-default)" }}>
            Quota Health
          </h1>
        </div>
        <div className="flex items-center gap-4 text-sm" style={{ color: "var(--color-label)" }}>
          {categorizedQuotas.nearLimit.length > 0 && (
            <span className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: HEALTH_COLORS.warning }} />
              {categorizedQuotas.nearLimit.length} near limit
            </span>
          )}
          {categorizedQuotas.exceeded.length > 0 && (
            <span className="flex items-center gap-1.5">
              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: HEALTH_COLORS.error }} />
              {categorizedQuotas.exceeded.length} exceeded
            </span>
          )}
        </div>
      </div>

      {quotas.length === 0 ? (
        <div
          className="rounded-lg p-8 text-center"
          style={{ backgroundColor: "var(--color-drop)", border: "1px solid var(--color-border)" }}
        >
          <div className="text-sm" style={{ color: "var(--color-label)" }}>
            No quotas configured
          </div>
        </div>
      ) : processedQuotas.length === 0 ? (
        <div
          className="rounded-lg p-8 text-center"
          style={{ backgroundColor: "var(--color-drop)", border: "1px solid var(--color-border)" }}
        >
          <div
            className="inline-flex items-center justify-center w-12 h-12 rounded-full mb-3"
            style={{ backgroundColor: "rgba(16, 185, 129, 0.1)" }}
          >
            <CheckCircle size={24} color={HEALTH_COLORS.ready} />
          </div>
          <div className="text-sm font-medium" style={{ color: "var(--color-default)" }}>
            All quotas are healthy
          </div>
          <div className="text-xs mt-1" style={{ color: "var(--color-label)" }}>
            {quotas.length} quotas within limits
          </div>
        </div>
      ) : (
        <div className="space-y-6">
          {/* Exceeded Quotas */}
          {categorizedQuotas.exceeded.length > 0 && (
            <div>
              <h2 className="text-sm font-medium mb-3" style={{ color: HEALTH_COLORS.error }}>
                Exceeded ({categorizedQuotas.exceeded.length})
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {categorizedQuotas.exceeded.map((quota) => (
                  <QuotaCard key={`${quota.name}-${quota.gvc}`} quota={quota} severity="error" />
                ))}
              </div>
            </div>
          )}

          {/* Near Limit Quotas */}
          {categorizedQuotas.nearLimit.length > 0 && (
            <div>
              <h2 className="text-sm font-medium mb-3" style={{ color: HEALTH_COLORS.warning }}>
                Near Limit ({categorizedQuotas.nearLimit.length})
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {categorizedQuotas.nearLimit.map((quota) => (
                  <QuotaCard key={`${quota.name}-${quota.gvc}`} quota={quota} severity="warning" />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

interface QuotaCardProps {
  quota: {
    name: string;
    description: string;
    gvc: string;
    remaining: number;
    error: boolean;
    warning: boolean;
  };
  severity: "error" | "warning";
}

const QuotaCard: React.FC<QuotaCardProps> = ({ quota, severity }) => {
  const Icon = severity === "error" ? AlertCircle : AlertTriangle;
  const color = severity === "error" ? HEALTH_COLORS.error : HEALTH_COLORS.warning;

  return (
    <div
      className="rounded-lg p-4"
      style={{
        backgroundColor: "var(--color-drop)",
        border: `1px solid ${color}`,
      }}
    >
      <div className="flex items-start gap-3">
        <Icon size={16} color={color} className="flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-sm font-medium" style={{ color: "var(--color-default)" }}>
              {quota.name}
            </span>
            {quota.gvc && (
              <span
                className="text-xs px-2 py-0.5 rounded"
                style={{ backgroundColor: "var(--color-border)", color: "var(--color-label)" }}
              >
                {quota.gvc}
              </span>
            )}
          </div>
          <div className="text-xs mb-2" style={{ color: "var(--color-label)" }}>
            {quota.description}
          </div>
          <div className="flex items-center gap-2">
            <span
              className="text-xs px-2 py-0.5 rounded"
              style={{
                backgroundColor: severity === "error" ? "rgba(239, 68, 68, 0.1)" : "rgba(245, 158, 11, 0.1)",
                color,
              }}
            >
              {quota.remaining} remaining
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
